import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import { Menu, ShoppingBag, ChevronDown, X, UserRound } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function Header() {
  const { settings, shopCategories: categories } = useSettings();
  const { count } = useCart();
  const { customer } = useCustomer();
  const accountPath = customer ? "/account" : "/account/login";
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const navigate = useNavigate();

  const brand = settings.brand_name || "Kys Boutique";

  const links = [
    { label: "Home", path: "/" },
    { label: "Ready to Ship", path: "/shop" },
    { label: "Build Your Set", path: "/builder" },
    { label: "About", path: "/about" },
    { label: "FAQ", path: "/faq" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-kys-blush-border/70 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Mobile menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-btn" className="lg:hidden" aria-label="Open menu">
              <Menu className="h-6 w-6 text-kys-ink" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[86%] max-w-sm bg-kys-cream p-0">
            <div className="flex items-center justify-between border-b border-kys-blush-border px-5 py-4">
              <span className="font-serif text-2xl">{brand}</span>
              <button onClick={() => setOpen(false)} aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex flex-col px-2 py-3">
              {links.map((l) => (
                <Link key={l.path} to={l.path} onClick={() => setOpen(false)}
                  data-testid={`mnav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="rounded px-3 py-3 text-base font-medium text-kys-charcoal hover:bg-kys-blush">
                  {l.label}
                </Link>
              ))}
              <Link to={accountPath} onClick={() => setOpen(false)} data-testid="mnav-account"
                className="rounded px-3 py-3 text-base font-medium text-kys-charcoal hover:bg-kys-blush">
                {customer ? "My Custom Orders" : "Sign in / Create account"}
              </Link>
              <div className="mt-2 border-t border-kys-blush-border px-3 pt-3">
                <p className="label-accent mb-2 text-kys-muted">Shop by category</p>
                {categories.map((c) => (
                  <Link key={c.id} to={`/shop?category=${c.id}`} onClick={() => setOpen(false)}
                    className="block rounded px-3 py-2 text-sm text-kys-charcoal hover:bg-kys-blush">
                    {c.name}
                  </Link>
                ))}
              </div>
            </nav>
          </SheetContent>
        </Sheet>

        {/* Brand */}
        <Link to="/" data-testid="brand-logo" className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0">
          <span className="font-serif text-2xl tracking-tight text-kys-ink sm:text-3xl">{brand}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex">
          <Link to="/" className="text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">Home</Link>
          <div className="relative" onMouseEnter={() => setShopOpen(true)} onMouseLeave={() => setShopOpen(false)}>
            <button data-testid="nav-shop" className="flex items-center gap-1 text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">
              Shop <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {shopOpen && (
              <div className="absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3">
                <div className="animate-fade-in rounded-md border border-kys-blush-border bg-white p-2 shadow-xl shadow-kys-blush-soft">
                  <Link to="/shop" className="block rounded px-3 py-2 text-sm font-medium text-kys-ink hover:bg-kys-blush">All Ready to Ship</Link>
                  {categories.map((c) => (
                    <Link key={c.id} to={`/shop?category=${c.id}`} className="block rounded px-3 py-2 text-sm text-kys-charcoal hover:bg-kys-blush">{c.name}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/builder" data-testid="nav-builder" className="text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">Build Your Set</Link>
          <Link to="/about" className="text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">About</Link>
          <Link to="/faq" className="text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">FAQ</Link>
          <Link to="/contact" className="text-sm font-medium text-kys-charcoal transition hover:text-kys-accent">Contact</Link>
        </nav>

        {/* Account + Cart */}
        <div className="flex items-center gap-4">
        <Link to={accountPath} data-testid="account-btn" aria-label="My account" className="hidden items-center gap-1.5 text-sm font-medium text-kys-charcoal transition-colors hover:text-kys-accent sm:flex">
          <UserRound className="h-5 w-5" />
          <span className="hidden xl:inline">{customer ? "My Orders" : "Sign in"}</span>
        </Link>
        <button data-testid="cart-btn" onClick={() => navigate("/cart")} className="relative" aria-label="Bag">
          <ShoppingBag className="h-6 w-6 text-kys-ink" />
          {count > 0 && (
            <span data-testid="cart-count" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-kys-accent text-[11px] font-semibold text-white">
              {count}
            </span>
          )}
        </button>
        </div>
      </div>
    </header>
  );
}
